import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Typography,
  Space,
  Divider,
  Alert,
  Descriptions,
  Steps,
  Modal
} from 'antd';
import {
  SearchOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from '../../utils/request';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Step } = Steps;

interface CouponInfo {
  id: string;
  code: string;
  name: string;
  expiryDate: string;
}

const CouponRedemption: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [couponInfo, setCouponInfo] = useState<CouponInfo | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [form] = Form.useForm();
  const [redemptionForm] = Form.useForm();

  const verifyCoupon = async (values: { code: string }) => {
    setVerifying(true);
    setErrorMessage(''); // 清除之前的错误信息
    
    try {
      const response = await axios.post('/api/coupons/verify', {
        code: values.code.trim()
      });

      if (response.data.valid) {
        setCouponInfo(response.data.coupon);
        setCurrentStep(1);
        message.success('优惠券验证成功', 5);
      }
    } catch (error: any) {
      console.log('验证错误:', error); // 添加调试日志
      const apiErrorMessage = error.response?.data?.message;
      console.log('错误信息:', apiErrorMessage); // 添加调试日志
      
      let displayMessage = '';
      // 根据不同错误类型显示不同的提示信息
      if (apiErrorMessage === '优惠码不存在') {
        displayMessage = '❌ 优惠码无效：该优惠码不存在，请检查后重新输入';
      } else if (apiErrorMessage === '优惠券已过期') {
        displayMessage = '⏰ 优惠券已过期：该优惠券已超过有效期，无法使用';
      } else if (apiErrorMessage === '优惠券已使用') {
        displayMessage = '✅ 优惠券已使用：该优惠券已被核销，无法重复使用';
      } else {
        displayMessage = apiErrorMessage || '验证失败，请稍后重试';
      }
      
      // 同时显示message和设置errorMessage状态
      message.error(displayMessage, 8);
      setErrorMessage(displayMessage);
      setCouponInfo(null);
    } finally {
      setVerifying(false);
    }
  };

  const redeemCoupon = async (values: { ekokNumber: string; customerName: string }) => {
    console.log('开始核销，优惠券信息:', couponInfo);
    console.log('核销参数:', values);
    
    if (!couponInfo) {
      console.error('没有优惠券信息');
      return;
    }

    setRedeeming(true);
    try {
      const response = await axios.post('/api/coupons/redeem', {
        code: couponInfo.code,
        ekokNumber: values.ekokNumber.trim(),
        customerName: values.customerName.trim()
      });
      
      console.log('核销成功响应:', response.data);
      message.success('🎉 优惠券核销成功！');
      setCurrentStep(2);
      
      setTimeout(() => {
        resetForm();
      }, 3000);

    } catch (error: any) {
      console.error('核销失败:', error);
      const errorMessage = error.response?.data?.message;
      
      // 根据不同错误类型显示不同的提示信息
      if (errorMessage === '优惠码不存在') {
        message.error('❌ 优惠码无效：该优惠码不存在');
      } else if (errorMessage === '优惠券已过期') {
        message.error('⏰ 优惠券已过期：该优惠券已超过有效期，无法核销');
      } else if (errorMessage === '优惠券已使用') {
        message.error('✅ 优惠券已使用：该优惠券已被核销，无法重复使用');
      } else {
        message.error(errorMessage || '核销失败，请稍后重试');
      }
    } finally {
      setRedeeming(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(0);
    setCouponInfo(null);
    setErrorMessage('');
    form.resetFields();
    redemptionForm.resetFields();
  };

  const confirmRedemption = () => {
    console.log('点击确认核销按钮');
    console.log('当前优惠券信息:', couponInfo);
    
    Modal.confirm({
      title: '确认核销',
      icon: <ExclamationCircleOutlined />,
      content: `确定要核销优惠券 "${couponInfo?.name}" (${couponInfo?.code}) 吗？`,
      okText: '确认核销',
      cancelText: '取消',
      onOk: () => {
        console.log('用户确认核销，提交表单');
        redemptionForm.submit();
      },
      onCancel: () => {
        console.log('用户取消核销');
      }
    });
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: '32px' }}>
        优惠券核销
      </Title>

      <Steps current={currentStep} style={{ marginBottom: '32px' }}>
        <Step title="输入优惠码" description="验证优惠券真伪" />
        <Step title="录入信息" description="填写EKOK号码和客人姓名" />
        <Step title="核销完成" description="成功核销优惠券" />
      </Steps>

      <Card style={{ minHeight: '400px' }}>
        {currentStep === 0 && (
          <div>
            <Title level={4}>第一步：输入优惠码</Title>
            
            {/* 显示错误信息 */}
            {errorMessage && (
              <Alert
                message="验证失败"
                description={errorMessage}
                type="error"
                showIcon
                closable
                onClose={() => setErrorMessage('')}
                style={{ marginBottom: '16px' }}
              />
            )}
            
            <Form
              form={form}
              layout="vertical"
              onFinish={verifyCoupon}
            >
              <Form.Item
                name="code"
                label="优惠码"
                rules={[{ required: true, message: '请输入优惠码' }]}
              >
                <Input
                  size="large"
                  placeholder="请输入优惠码"
                  prefix={<SearchOutlined />}
                  style={{ fontSize: '16px' }}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={verifying}
                  size="large"
                  block
                >
                  验证优惠券
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}

        {currentStep === 1 && couponInfo && (
          <div>
            <Title level={4}>第二步：确认优惠券信息并填写核销信息</Title>
            
            <Alert
              message="优惠券验证成功"
              type="success"
              showIcon
              style={{ marginBottom: '24px' }}
            />

            <Descriptions 
              bordered 
              column={1} 
              style={{ marginBottom: '24px' }}
              title="优惠券信息"
            >
              <Descriptions.Item label="优惠券名称">
                <Text strong style={{ fontSize: '16px' }}>{couponInfo.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="优惠码">
                <Text code style={{ fontSize: '14px' }}>{couponInfo.code}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="有效期至">
                <Text>{dayjs(couponInfo.expiryDate).format('YYYY年MM月DD日')}</Text>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Form
              form={redemptionForm}
              layout="vertical"
              onFinish={redeemCoupon}
            >
              <Form.Item
                name="ekokNumber"
                label="EKOK号码"
                rules={[
                  { required: true, message: '请输入EKOK号码' },
                  { pattern: /^\d+$/, message: 'EKOK号码只能包含数字' }
                ]}
              >
                <Input
                  size="large"
                  placeholder="请输入EKOK号码"
                  style={{ fontSize: '16px' }}
                />
              </Form.Item>

              <Form.Item
                name="customerName"
                label="客人姓名"
                rules={[
                  { required: true, message: '请输入客人姓名' },
                  { min: 2, message: '姓名至少2个字符' }
                ]}
              >
                <Input
                  size="large"
                  placeholder="请输入客人姓名"
                  style={{ fontSize: '16px' }}
                />
              </Form.Item>

              <Form.Item>
                <Space size="large">
                  <Button size="large" onClick={resetForm}>
                    重新开始
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                      console.log('按钮被点击');
                      // 先验证表单，然后直接核销
                      redemptionForm.validateFields().then((values) => {
                        console.log('表单验证成功:', values);
                        redeemCoupon(values);
                      }).catch((error) => {
                        console.error('表单验证失败:', error);
                        message.error('请填写完整的信息');
                      });
                    }}
                    loading={redeeming}
                  >
                    确认核销
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}

        {currentStep === 2 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <CheckCircleOutlined 
              style={{ fontSize: '72px', color: '#52c41a', marginBottom: '24px' }} 
            />
            <Title level={3} style={{ color: '#52c41a' }}>
              核销成功！
            </Title>
            <Text style={{ fontSize: '16px', color: '#666' }}>
              优惠券已成功核销，系统将在3秒后自动重置
            </Text>
            <div style={{ marginTop: '24px' }}>
              <Button type="primary" size="large" onClick={resetForm}>
                继续核销
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CouponRedemption;